import { stripSceneNumberPrefix } from '@/lib/imageStorySceneOrder';

/** OCR / caption / dialogue text read from the storyboard panel (reference only). */
export function panelTextForScene(scene) {
  const explicit = stripSceneNumberPrefix(String(scene?.panel_text || '').trim());
  if (explicit) return explicit;
  const visual = stripSceneNumberPrefix(String(scene?.visual || '').trim());
  if (visual) return visual;
  return stripSceneNumberPrefix(
    String(scene?.text || scene?.narration_text || scene?.scene_text || '').trim(),
  );
}

/** Spoken narration line for TTS / video sync. */
export function narrationTextForScene(scene) {
  if (sceneSkipsNarration(scene)) return '';
  return stripSceneNumberPrefix(
    String(scene?.text || scene?.narration_text || scene?.scene_text || '').trim(),
  );
}

/** User-authored script for subtitle burn-in (not transcribed audio or TTS-adapted text). */
export function narrationAuthoredScriptText(scene) {
  const authored = stripSceneNumberPrefix(String(scene?.authored_script || '').trim());
  if (authored) return authored;
  return narrationTextForScene(scene);
}

/** Visual-only scene (no TTS / spoken line). */
export function sceneSkipsNarration(scene) {
  return Boolean(scene?.skip_narration);
}

/** True when every scene is marked visual-only (no narration anywhere). */
export function allScenesSkipNarration(scenes) {
  const list = Array.isArray(scenes) ? scenes : [];
  return list.length > 0 && list.every((scene) => sceneSkipsNarration(scene));
}

/** @param {Record<string, unknown>} scene @param {boolean} skip */
export function toggleSceneSkipNarration(scene, skip) {
  const next = { ...scene, skip_narration: Boolean(skip) };
  if (skip && !Number(next.duration)) {
    next.duration = 3;
  }
  return next;
}

/** @param {Array<Record<string, unknown>>} scenes @param {boolean} skip */
export function applySkipNarrationToAllScenes(scenes, skip) {
  return (scenes || []).map((scene) => toggleSceneSkipNarration(scene, skip));
}

/** @param {Record<string, unknown>} scene @param {number} seconds */
export function setSceneSilentDuration(scene, seconds) {
  const parsed = Number(seconds);
  const duration = Number.isFinite(parsed) && parsed > 0 ? Math.min(60, parsed) : 3;
  return { ...scene, duration, skip_narration: true };
}

/** @param {Record<string, unknown>} scene @param {string} narrationText */
export function applyNarrationToScene(scene, narrationText) {
  const body = stripSceneNumberPrefix(String(narrationText ?? '').trim());
  return {
    ...scene,
    text: body,
    narration_text: body,
    scene_text: body,
  };
}

/** @param {Array<Record<string, unknown>>} scenes */
export function buildSceneScriptDrafts(scenes) {
  return [...(scenes || [])]
    .sort(
      (a, b) => (Number(a.segment_number) || 0) - (Number(b.segment_number) || 0),
    )
    .map((scene) => ({
      segment_number: Number(scene.segment_number) || 0,
      client_id: scene.client_id || null,
      image_url: scene.image_url || null,
      crop_revision: scene.crop_revision,
      crop_box: scene.crop_box,
      panel_text: panelTextForScene(scene),
      narration: narrationTextForScene(scene),
      skip_narration: sceneSkipsNarration(scene),
      duration: Number(scene.duration) || 3,
    }));
}
